import {
    CustomCodeUtilService,
    DROPDOWN_DOWN_ICON_CLASS,
    ITask,
    ITaskContainer,
    LanguagesService,
    PAGE_SIZE_OPTIONS,
    ProjectService,
} from '@alpha-core/platform-ui';
import { PlatformChipComponent } from '@alpha-core/platform-ui/components/chip.component';
import { PlatformCountDropdownComponent } from '@alpha-core/platform-ui/components/count-dropdown';
import {
    ADMIN_INBOX_CONFIGS,
    INBOX_LIST_TYPE,
    InboxFilterType,
} from '@alpha-core/platform-ui/interfaces/admin-filter.interface';
import { FilterService } from '@alpha-core/platform-ui/services/filter.service';
import { Component, DestroyRef, QueryList, ViewChildren, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { AdminFilterService } from '../../services/admin-filter.service';
import { CaseService } from '../../services/case.service';
import { BUTTON, DROPDOWN, UPDATE_FILTER } from '../../services/constants/filter-config.constant';
import { TASK_STATUSES } from '../../services/constants/task-status.constants';
import { FilterConfigService } from '../../services/filter-config.service';
import { InboxService } from '../../services/inbox.service';
import { userService } from '../../services/user-service';
import { AlphaSharedModule } from '../../shared/alpha-shared.module';
import { IRecovery, InboxTaskType, RootConfig, TaskData } from '../../shared/interfaces/recovery-task.interface';
import { DashboardComponent } from '../dashboard/dashboard.component';
import { DashboardService } from '../dashboard/dashboard.service';
import { InboxColumnsComponent } from './inbox-columns/inbox-columns.component';
import { FilterSaveComponent } from './inbox-filters/filter-save/filter-save.component';
import { InboxFiltersComponent } from './inbox-filters/inbox-filters.component';
import { InboxListComponent } from './inbox-list/inbox-list.component';
import { InboxTableComponent } from './inbox-table/inbox-table.component';

@Component({
    selector: 'app-inbox',
    standalone: true,
    imports: [
        AlphaSharedModule,
        InboxTableComponent,
        InboxColumnsComponent,
        InboxFiltersComponent,
        DashboardComponent,
        InboxListComponent,
        PlatformChipComponent,
        PlatformCountDropdownComponent,
    ],
    templateUrl: './inbox.component.html',
})
export class InboxComponent {
    filterConfigService = inject(FilterConfigService);
    filterEditFilterLabel!: string;
    defaultFilterValue!: RootConfig;
    buttonOrDropdown: string = BUTTON;
    @ViewChildren('filterComponent') filterComponent!: QueryList<InboxFiltersComponent>;

    project: any;
    caseInstances: any[] = [];
    selectedDropdown: 'tasks' | 'signals' | 'recovery' = 'tasks';
    filterOptions: Record<'pageNumber' | 'pageSize' | 'totalCases', number> = {
        pageNumber: 0,
        pageSize: 5,
        totalCases: 0,
    };
    tasks: any[] | undefined;
    inboxTask: any;
    currentTask: any;
    filterData: Record<string, any>[] = [];
    userName: any;
    dashboardConfig: { display: boolean; collapsible: boolean; cards: Array<any> } | null = null;
    filterObject: Record<string, any> = {};
    filterChipsConfig: Array<{
        value: string;
        key: string;
        keys: Array<string>;
        type: string;
    }> = [];
    originalInboxConfig: any;
    dropdownIconClass = DROPDOWN_DOWN_ICON_CLASS;
    pageSizeOptions: Array<{ value: number; viewValue: string }> = PAGE_SIZE_OPTIONS;
    visibleChipCount = 2;

    private destroyRef = inject(DestroyRef);
    selectedTask: any;
    pagination = {
        disablePaginationNext: false,
        disablePaginationPrevious: false,
        rangeStart: 0,
        rangeEnd: 0,
    };

    taskConfigs: Array<ADMIN_INBOX_CONFIGS> = [
        { key: 'tasks', name: 'default-cm-tasks', default: false },
        { key: 'signals', name: 'default-cm-signals', default: false },
        { key: 'recovery', name: 'default-cm-re-attempt', default: false },
    ];

    private inboxService = inject(InboxService);
    private caseService = inject(CaseService);
    private projectService = inject(ProjectService);
    private languageService = inject(LanguagesService);
    private dashboardService = inject(DashboardService);
    private userService = inject(userService);
    private messageService = inject(MessageService);
    private dialogService = inject(DialogService);
    private customCodeUtilService = inject(CustomCodeUtilService);
    private filterService = inject(FilterService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private adminFilterService = inject(AdminFilterService);
    savedFilterField: any;

    constructor() {}

    async ngOnInit(): Promise<void> {
        this.editSubscription();
        this.subscribeToTaskListRefresh();
        this.languageSubscription();
        const inboxTask = await lastValueFrom(this.inboxService.getAdminInbox());
        this.originalInboxConfig = structuredClone(inboxTask);
        this.inboxTask = this.languageService.translateConfig(this.originalInboxConfig);
        this.project = this.projectService.fetchCurrentProject();
        const metadata = await lastValueFrom(this.userService.getUserMetadata());
        const enabledListTypes = Object.values(INBOX_LIST_TYPE).filter((type) => this.inboxTask[type]?.enabled);
        await this.adminFilterService.fetchAdminListFilters(enabledListTypes);
        await this.loadInboxWithFilters();
        const adminDefaultTask = metadata?.metadata[0].metadata?.adminDefaultTask?.[this.project.projectId];
        if (!this.selectedDropdown) {
            this.selectedDropdown = adminDefaultTask?.key || 'tasks';
        }
        await this.initaliseInboxParams(this.selectedDropdown);
        if (Object.keys(this.filterService.queryParams).length) {
            if (!this.currentTask?.length) this.currentTask = this.inboxTask[this.selectedDropdown];
            this.dashboardConfig = this.inboxTask[this.selectedDropdown]?.dashboardConfig;
            this.onApplyFilterChange(this.filterService.filters, Boolean(this.dashboardConfig?.display));
        } else {
            this.setAdminInboxTable(this.selectedDropdown);
        }
        this.projectService
            .getSavedList()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async () => {
                if (!this.selectedTask && metadata?.metadata[0].metadata?.adminDefaultTask?.[this.project.projectId]) {
                    this.selectedTask = metadata?.metadata[0].metadata?.adminDefaultTask?.[this.project.projectId];
                }
            });
        this.getPageSizeOpts();
        if (this.selectedDropdown === 'recovery') {
            this.fetchAndUpdateCaseData();
        }
    }

    async loadInboxWithFilters() {
        const inboxType = this.filterService.getInboxTypeFromRoute(this.route.snapshot);
        const inbox = this.inboxTask[inboxType];
        const isEnabled = inbox?.enabled === true;

        if (
            !isEnabled ||
            !inbox ||
            (this.filterService?.savedFilter && !Object.keys(this.filterService?.filters).length)
        ) {
            this.redirectToDefaultInbox();
            return;
        }

        this.updateSelectedTask(this.filterService.inboxType, this.route.snapshot.queryParams);

        const fields = inbox.filters?.[0]?.fields || [];
        this.filterService.initFromRoute(this.route, fields);
    }

    redirectToDefaultInbox() {
        const projectId = this.project.projectId;
        const metadata = this.userService.usersMetadata?.metadata?.[0]?.metadata || {};

        const adminDefaultTask = metadata?.adminDefaultTask?.[projectId];

        const fallbackTask = this.taskConfigs.find((t) => this.inboxTask[t.key]?.enabled);
        const savedFilterExist = this.getSavedFilter(adminDefaultTask?.name);

        const inboxType =
            (adminDefaultTask && this.inboxTask[adminDefaultTask.key]?.enabled && savedFilterExist
                ? adminDefaultTask.key
                : fallbackTask?.key) || INBOX_LIST_TYPE.TASKS;

        const defaultTask = inboxType === adminDefaultTask?.key && savedFilterExist ? adminDefaultTask : fallbackTask;

        this.filterService.inboxType = inboxType;
        this.filterService.filters = savedFilterExist && defaultTask?.filterObject ? defaultTask.filterObject : {};

        this.handleTaskData(defaultTask);

        if (!defaultTask?.filterObject) {
            this.router.navigate(['/home/cases', inboxType], { queryParams: { projectId }, replaceUrl: true });
        }
    }

    private async updateSelectedTask(key: string, queryParams?: Record<string, any>) {
        this.filterService.inboxType = key as InboxTaskType;
        this.selectedDropdown = key as InboxTaskType;
        const projectId = this.project.projectId;

        if (queryParams?.['saved_filter']) {
            const filter = this.getSavedFilter(queryParams?.['saved_filter']);

            if (filter) {
                this.selectedTask = filter;
                this.selectedDropdown = filter.key as InboxTaskType;
                this.filterService.inboxType = filter.key as InboxTaskType;
                this.filterService.applyFilter(filter as ITask, this.route, projectId);
                this.buttonOrDropdown = filter.type === InboxFilterType.ADMIN_FILTER ? BUTTON : DROPDOWN;
                if (filter.type === InboxFilterType.ADMIN_FILTER && filter?.filterId) {
                    const res: any = await lastValueFrom(this.adminFilterService.getFilterById(filter.filterId));
                    let allFilters = { ...(res?.additionalFilters || {}), ...(res?.filters || {}) };
                    allFilters = this.languageService.translateConfig(allFilters);
                    this.currentTask = this.inboxTask[this.selectedDropdown];
                    this.currentTask.filters[0].fields =
                        this.currentTask?.filters?.[0]?.fields?.filter((field: any) =>
                            allFilters.hasOwnProperty(field.name),
                        ) || [];

                    this.savedFilterField = this.currentTask.filters[0].fields;
                }
                return;
            } else {
                this.redirectToDefaultInbox();
                return;
            }
        }

        this.selectedTask = this.taskConfigs.find((t) => t['key'] === this.selectedDropdown);
    }

    getSavedFilter(filterName: string) {
        const savedFilters: ITaskContainer =
            this.userService.usersMetadata?.metadata?.[0].metadata?.adminSavedFilters?.[this.project.projectId] || {};
        const allFilters = Object.values(savedFilters).flat() as ITask[];
        const adminFilters = this.adminFilterService.adminFilters || [];
        const allSavedFilters = [...allFilters, ...adminFilters];
        const filter = allSavedFilters.find((f) => f.name === filterName);

        return filter;
    }

    editSubscription() {
        this.filterConfigService.editSubject$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: RootConfig) => {
                this.filterEditFilterLabel = data.filter.name;
                this.defaultFilterValue = data;
            });
    }

    subscribeToTaskListRefresh() {
        this.projectService
            .getTask()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: any) => {
                this.handleTaskData(data);
            });
    }

    async handleTaskData(data: TaskData) {
        this.selectedDropdown = data.key;
        this.selectedTask = data;

        this.resetFilterOptions();
        if (
            !Object.keys(data?.filterObject || {}).length &&
            data.type === InboxFilterType.ADMIN_FILTER &&
            data.filterId
        ) {
            const response: any = await lastValueFrom(this.adminFilterService.getFilterById(data.filterId));
            data.filterObject = { ...(response?.additionalFilters || {}), ...(response?.filters || {}) };
        }
        if (data?.filterObject) {
            if (data.type === InboxFilterType.ADMIN_FILTER) {
                this.buttonOrDropdown = BUTTON;
                this.filterCurrentTaskFilters(data.filterObject);
            } else {
                this.currentTask = this.originalInboxConfig?.[this.selectedDropdown];
                this.buttonOrDropdown = DROPDOWN;
            }
            this.onApplyFilterChange(data.filterObject);
        } else {
            this.resetFilterSettings();
            this.setAdminInboxTable(this.selectedDropdown);
        }
    }

    filterCurrentTaskFilters(filterValue: Record<string, any>) {
        const currentTask = this.originalInboxConfig[this.selectedDropdown];

        const filteredTask = {
            ...currentTask,
            filters: currentTask.filters?.map((filter: any) => ({
                ...filter,
                fields: filter.fields.filter((field: any) => {
                    return !!filterValue[field.name];
                }),
            })),
        };
        this.currentTask = filteredTask;
    }

    resetFilterOptions() {
        this.filterOptions = {
            pageNumber: 0,
            pageSize: this.filterOptions?.pageSize || this.inboxTask[this.selectedDropdown]?.rowNumber,
            totalCases: 0,
        };
    }

    resetFilterSettings() {
        this.filterObject = {};
        this.buttonOrDropdown = BUTTON;
        this.filterChipsConfig = [];
    }

    triggerSelect(item: any, dropdown: any) {
        const firstFilterComponent = this.filterComponent.first;
        if (firstFilterComponent) {
            const formValues = firstFilterComponent.getFormValues();
            if (item.label == UPDATE_FILTER) {
                this.onSaveFilter({ isEdit: true, formValues });
            } else {
                this.filterEditFilterLabel = '';
                this.onSaveFilter({ isEdit: false, formValues });
            }
        }
        dropdown.hide();
    }

    languageSubscription() {
        this.languageService.selectedLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((language) => {
            const resetFilter = false;
            this.setAdminInboxTask(resetFilter);
            if (language) {
                this.getPageSizeOpts();
            }
        });
    }

    private async initaliseInboxParams(selectedTaskCategory = 'tasks') {
        this.currentTask = [];
        this.filterData = [];
        await this.dashboardService.setTaskCategory(selectedTaskCategory);
        this.currentTask = this.inboxTask[selectedTaskCategory];
        this.dashboardConfig = this.inboxTask[selectedTaskCategory].dashboardConfig;
        this.setFilterOptions(0, this.inboxTask[selectedTaskCategory].rowNumber);
    }

    setAdminInboxTask(resetFilter = true) {
        const inboxConfig = structuredClone(this.originalInboxConfig);
        this.inboxTask = this.languageService.translateConfig(inboxConfig);
        if (this.inboxTask) {
            if (this.filterData.length) {
                this.onApplyFilterChange(this.filterObject);
            } else {
                this.setAdminInboxTable(this.selectedDropdown, resetFilter);
            }
        }
    }

    setAdminInboxTable(selectedTaskCategory: string, resetFilter = true) {
        this.currentTask = [];
        this.filterData = [];
        this.dashboardService.setTaskCategory(selectedTaskCategory);
        switch (selectedTaskCategory) {
            case 'tasks':
                this.currentTask = this.originalInboxConfig.tasks;
                this.dashboardConfig = this.inboxTask.tasks.dashboardConfig;
                if (resetFilter) {
                    this.setFilterOptions(0, this.inboxTask.tasks.rowNumber); // Set filter options
                }
                this.fetchAndLoadTasks('tasks');
                break;
            case 'signals':
                this.currentTask = this.originalInboxConfig.signals;
                this.dashboardConfig = this.inboxTask.signals.dashboardConfig;
                if (resetFilter) {
                    this.setFilterOptions(0, this.inboxTask.signals.rowNumber); // Set filter options
                }
                this.fetchAndLoadTasks('signals');
                break;
            case 'recovery':
                this.currentTask = this.originalInboxConfig.recovery;
                if (resetFilter) {
                    this.setFilterOptions(0, this.inboxTask.recovery.rowNumber); // Set filter options
                }
                this.fetchAndUpdateCaseData();
                this.fetchAndLoadTasks('recovery');
                break;
            default:
                break;
        }
    }

    private fetchAndLoadTasks(taskType: string) {
        switch (taskType) {
            case 'tasks':
                this.getAdminTasks(
                    {
                        pageNumber: 0,
                        pageSize: this.inboxTask.tasks.rowNumber,
                    },
                    [],
                    true,
                    this.inboxTask.tasks.dashboardConfig?.display,
                );
                break;
            case 'signals':
                this.getSignals(
                    {
                        pageNumber: 0,
                        pageSize: this.inboxTask.signals.rowNumber,
                    },
                    [],
                    this.inboxTask?.signals?.dashboardConfig?.display ?? false,
                );
                break;
            case 'recovery':
                this.getRecovery(
                    {
                        pageNumber: 0,
                        pageSize: this.inboxTask.recovery.rowNumber,
                    },
                    [],
                );
                break;
            default:
                break;
        }
    }

    private setFilterOptions(pageNumber: number, pageSize: number) {
        this.filterOptions = {
            pageNumber: pageNumber,
            pageSize: pageSize,
            totalCases: 0,
        };
    }

    getSignals(
        paginationDetails?: { pageNumber: number; pageSize: number },
        filterObject?: Record<string, any>[],
        fetchDashboard: boolean = false,
    ) {
        const mappingPaths = this.caseService.reStrucetureMappingData(this.inboxTask, this.selectedDropdown);
        if (fetchDashboard) {
            this.caseService.fetchDashboardStatus({ status: TASK_STATUSES });
        }
        this.caseService
            .fetchSignals({
                pageNumber: paginationDetails?.pageNumber,
                pageSize: paginationDetails?.pageSize,
                enquiryFields: filterObject,
                mappingPaths: mappingPaths,
            })
            .subscribe({
                next: async (result: any) => {
                    ((this.caseInstances = result.response), (this.filterOptions.totalCases = result?.count));
                    this.handlePaginationDetails();
                    this.customCodeUtilService.checkReadyAndExecuteHooks(this.executeOnInitHooks);
                },
                error: (error: any) => {
                    this.caseInstances = [];
                    console.log('Error fetching signals:', error);
                },
            });
    }

    getRecovery(paginationDetails?: { pageNumber: number; pageSize: number }, filterObject?: Record<string, any>[]) {
        const mappingPaths = this.caseService.reStrucetureMappingData(this.inboxTask, this.selectedDropdown);
        if (!Array.isArray(this.currentTask.tasks) || !this.currentTask.tasks.length) {
            this.caseInstances = [];
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No Tasks configured to fetch',
            });
            return;
        }
        const taskNames = this.currentTask.tasks.map((el: IRecovery) => el.taskName);
        this.caseService
            .fetchReAttemptTasks({
                pageNumber: paginationDetails?.pageNumber,
                pageSize: paginationDetails?.pageSize,
                enquiryFields: filterObject,
                taskNames: taskNames,
                mappingPaths: mappingPaths,
            })
            .subscribe({
                next: async (result: any) => {
                    if (Array.isArray(result)) {
                        this.caseInstances = result;
                        this.customCodeUtilService.checkReadyAndExecuteHooks(this.executeOnInitHooks);
                    } else {
                        console.error('Error fetching case instances:', result);
                    }
                },
                error: (error: any) => {
                    this.caseInstances = [];
                    console.log('Error fetching recovery:', error);
                },
            });
        this.caseService
            .fetchTasksCount({
                enquiryFields: filterObject,
                taskNames: taskNames,
            })
            .subscribe((result: any) => {
                this.filterOptions.totalCases = result?.count;
                this.handlePaginationDetails();
            });
    }

    executeOnInitHooks = async () => {
        if (this.inboxTask?.hooks?.onInit) {
            await this.inboxService.executeAction(this.inboxTask.hooks.onInit.triggers, {
                data: this.caseInstances,
            });
        }
    };

    getAdminTasks(
        paginationDetails?: { pageNumber: number; pageSize: number },
        filterObject?: Record<string, any>[],
        fetchCount: boolean = false,
        fetchDashboard: boolean = false,
    ) {
        const excludeTaskNames = this.inboxTask?.recovery?.tasks?.map?.((el: IRecovery) => el.taskName);
        const mappingPaths = this.caseService.reStrucetureMappingData(this.inboxTask, this.selectedDropdown);
        this.caseService
            .fetchAdminTasks({
                pageNumber: paginationDetails?.pageNumber,
                pageSize: paginationDetails?.pageSize,
                enquiryFields: filterObject,
                excludeTaskNames,
                mappingPaths: mappingPaths,
            })
            .subscribe({
                next: async (result: any) => {
                    if (Array.isArray(result)) {
                        this.caseInstances = result;
                        this.customCodeUtilService.checkReadyAndExecuteHooks(this.executeOnInitHooks);
                    } else {
                        console.error('Error fetching case instances:', result);
                    }
                },
                error: (error: any) => {
                    this.caseInstances = [];
                    console.log('Error fetching caseInstaces:', error);
                },
            });
        if (fetchDashboard) {
            this.caseService.fetchDashboardStatus({ status: TASK_STATUSES });
        }
        if (fetchCount) {
            this.caseService
                .fetchTasksCount({ enquiryFields: filterObject, excludeTaskNames })
                .subscribe((result: any) => {
                    this.filterOptions.totalCases = result?.count;
                    this.handlePaginationDetails();
                });
        }
    }

    // apply column config to table
    applyColumn(array: any) {
        this.currentTask.columns = array;
        this.currentTask = structuredClone(this.currentTask);
    }

    refresh() {
        this.getUserTaskOnSelectedDropdown();
    }

    getTasks(event: any) {
        if (event.taskName === 'tasks') {
            this.getAdminTasks(event, this.filterData);
        } else if (event.taskName === 'signals') {
            this.getSignals(event, this.filterData);
        } else {
            this.getRecovery(event, this.filterData);
        }
    }

    onApplyFilterChange(filterData: any, fetchDashboard: boolean = false) {
        this.filterObject = filterData;
        const filterObject: any = [];
        this.filterChipsConfig = [];
        for (const key in filterData) {
            if (filterData[key].type === 'search' && filterData[key].value && filterData[key].value.length) {
                filterObject.push({
                    mapping: this.inboxService.cleanMappingPathString(filterData[key].mapping),
                    mappingType: filterData[key].mappingType,
                    filterOptions: {
                        type: filterData[key].type,
                        value: filterData[key].value,
                        _exactMatch: filterData[key]?._exactMatch,
                    },
                });
                const obj = {
                    key,
                    value: `${filterData[key].label}: ${filterData[key].value}`,
                    keys: ['value'],
                    type: filterData[key].type,
                };
                this.filterChipsConfig.push(obj);
            } else if (
                filterData[key].type === 'number-range' &&
                filterData[key].maxLabel &&
                filterData[key].minLabel
            ) {
                filterObject.push({
                    mapping: this.inboxService.cleanMappingPathString(filterData[key].mapping),
                    mappingType: filterData[key].mappingType,
                    filterOptions: {
                        type: filterData[key].type,
                        minValue: filterData[key].minLabel,
                        maxValue: filterData[key].maxLabel,
                    },
                });
                const obj = {
                    key,
                    value: `${filterData[key].label}: ${filterData[key].minLabel} - ${filterData[key].maxLabel}`,
                    keys: ['minLabel', 'maxLabel'],
                    type: filterData[key].type,
                };
                this.filterChipsConfig.push(obj);
            } else if (filterData[key].type === 'date-range' && filterData[key].value && filterData[key].value.length) {
                filterObject.push({
                    mapping: this.inboxService.cleanMappingPathString(filterData[key].mapping),
                    mappingType: filterData[key].mappingType,
                    filterOptions: {
                        type: filterData[key].type,
                        minValue: filterData[key].value[0],
                        maxValue: filterData[key].value[1],
                    },
                });
                const obj = {
                    key,
                    value: `${filterData[key].label}: ${filterData[key].value[0]} - ${filterData[key].value[1]}`,
                    keys: ['value'],
                    type: filterData[key].type,
                };
                this.filterChipsConfig.push(obj);
            } else if (filterData[key].type === 'enum') {
                const value = [];
                for (const values in filterData[key]) {
                    if (filterData[key][values] === true && values !== '_exactMatch') {
                        value.push(values);
                    }
                }
                if (value.length) {
                    filterObject.push({
                        mapping: this.inboxService.cleanMappingPathString(filterData[key].mapping),
                        mappingType: filterData[key].mappingType,
                        filterOptions: {
                            type: filterData[key].type,
                            value,
                            _exactMatch: filterData[key]?._exactMatch,
                        },
                    });
                    const obj = {
                        key,
                        value: filterData[key].label + ': ' + value.join(', '),
                        keys: value,
                        type: filterData[key].type,
                    };
                    this.filterChipsConfig.push(obj);
                }
            } else if (filterData[key].type === 'boolean') {
                const value = [];
                for (const values in filterData[key]) {
                    if (filterData[key][values] === true) {
                        value.push(values.toLowerCase());
                    }
                }
                if (value.length) {
                    filterObject.push({
                        mapping: this.inboxService.cleanMappingPathString(filterData[key].mapping),
                        mappingType: filterData[key].mappingType,
                        filterOptions: {
                            type: filterData[key].type,
                            value,
                        },
                    });
                    const obj = {
                        key,
                        value: filterData[key].label + ': ' + value.join(', '),
                        keys: value,
                        type: filterData[key].type,
                    };
                    this.filterChipsConfig.push(obj);
                }
            }
        }
        this.filterData = filterObject;
        this.filterService.inboxType = this.selectedTask.key;
        this.filterService.updateUrl(filterData, this.selectedTask, this.route);
        this.getUserTaskOnSelectedDropdown(fetchDashboard);
    }

    onSaveFilter({ isEdit, formValues }: any = {}) {
        this.filterEditFilterLabel = this.selectedTask.name;
        if (!Object.keys(this.filterObject).length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No Filter to Save',
            });
            return;
        }
        const projectId = this.projectService.fetchCurrentProject()?.projectId;
        if (!projectId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No Project Selected',
            });
            return;
        }
        this.userService.getUserMetadata().subscribe({
            next: (res: any) => {
                const metadata: any = res.metadata[0].metadata;
                metadata.adminSavedFilters = {
                    ...metadata?.adminSavedFilters,
                    [projectId]: {
                        ...metadata?.adminSavedFilters?.[projectId],
                    },
                };
                const projectSavedFilters = metadata.adminSavedFilters[projectId] || {};
                const presentNames: string[] = [];
                Object.keys(projectSavedFilters).forEach((key) => {
                    projectSavedFilters[key].forEach((value: any) => {
                        presentNames.push(value.name);
                    });
                });
                presentNames.push(...['Tasks', 'Signals']);
                this.dialogService
                    .open(FilterSaveComponent, {
                        width: '30rem',
                        header: this.languageService.getTranslation('default-cm-saveFilter') || 'Save Filter',
                        data: {
                            presentNames,
                            currentSelectedFilter: isEdit ? this.filterEditFilterLabel : '',
                        },
                    })
                    .onClose.subscribe((filterName: string) => {
                        if (!filterName) return;
                        const data = {
                            name: filterName,
                            key: this.selectedDropdown,
                            default: isEdit ? this.defaultFilterValue?.filter?.default : false,
                            filterObject: this.filterObject,
                        };
                        if (!Array.isArray(projectSavedFilters[this.selectedDropdown])) {
                            projectSavedFilters[this.selectedDropdown] = [];
                        }
                        if (isEdit) {
                            projectSavedFilters[this.selectedDropdown] = projectSavedFilters[this.selectedDropdown].map(
                                (element: any) => {
                                    if (element.name === this.filterEditFilterLabel) {
                                        data.default = element.default;
                                        element = data;
                                        this.filterEditFilterLabel = data.name;
                                    }
                                    return element;
                                },
                            );
                            if (data.default) {
                                metadata.adminDefaultTask[projectId] = data;
                            }
                        } else {
                            this.filterEditFilterLabel = data.name;

                            projectSavedFilters[this.selectedDropdown].push(data);
                        }
                        this.userService.updateUserMetadata(metadata).subscribe({
                            complete: () => {
                                this.projectService.savedListUpdated('savedFilterUpdate');
                                this.messageService.add({
                                    severity: 'success',
                                    summary: 'success',
                                    detail: 'Filter Saved SuccessFully',
                                });
                            },
                            error: (err) => console.error(err),
                        });

                        this.filterConfigService.selectedInboxListUpdate.next(data);

                        this.selectedTask = data;

                        if (data?.filterObject) {
                            this.filterObject = data.filterObject;
                            this.buttonOrDropdown = DROPDOWN;
                            this.filterService.updateUrl(this.filterObject, data, this.route);
                        }
                    });
            },
        });
    }

    removeFilterChip(index: any) {
        const [data] = this.filterChipsConfig.splice(index, 1);
        this.filterData.splice(index, 1);
        data.keys.forEach((key) => {
            if (data.type === 'boolean') {
                if (key === 'true') {
                    this.filterObject[data.key]['True'] = false;
                } else {
                    this.filterObject[data.key]['False'] = false;
                }
            } else {
                this.filterObject[data.key][key] = undefined;
            }
        });
        this.filterObject = { ...this.filterObject };
        this.filterService.updateUrl(this.filterData, this.selectedTask, this.route);
        this.getUserTaskOnSelectedDropdown();
        if (!this.filterData.length && this.selectedTask?.type == InboxFilterType.ADMIN_FILTER) {
            this.redirectToDefaultInbox();
        }
    }

    getUserTaskOnSelectedDropdown(fetchDashboard: boolean = false) {
        if (this.selectedDropdown === 'signals') {
            this.getSignals({ pageNumber: 0, pageSize: this.filterOptions.pageSize }, this.filterData, fetchDashboard);
        } else if (this.selectedDropdown === 'tasks') {
            this.getAdminTasks(
                { pageNumber: 0, pageSize: this.filterOptions.pageSize },
                this.filterData,
                true,
                fetchDashboard,
            );
        } else if (this.selectedDropdown === 'recovery') {
            this.getRecovery({ pageNumber: 0, pageSize: this.filterOptions.pageSize }, this.filterData);
        }
        this.filterOptions = {
            ...this.filterOptions,
            pageNumber: 0,
        };
    }

    // used for pagination
    changePage(changeType: 'increment' | 'decrement') {
        if (changeType == 'increment') this.filterOptions.pageNumber++;
        else this.filterOptions.pageNumber--;
        this.emitPaginationDetails();
    }

    emitPaginationDetails() {
        this.handlePaginationDetails();
        this.getTasks({
            pageNumber: this.filterOptions.pageNumber,
            pageSize: this.filterOptions.pageSize,
            taskName: this.selectedDropdown,
        });
    }

    handlePaginationDetails() {
        this.pagination.disablePaginationNext =
            this.filterOptions.pageSize * (this.filterOptions.pageNumber + 1) >= this.filterOptions.totalCases ||
            this.filterOptions.totalCases < this.filterOptions.pageSize;
        this.pagination.disablePaginationPrevious = this.filterOptions.pageSize * this.filterOptions.pageNumber == 0;
        this.pagination.rangeStart = this.filterOptions.pageSize * this.filterOptions.pageNumber + 1;
        this.pagination.rangeEnd =
            this.filterOptions.pageSize * (this.filterOptions.pageNumber + 1) > this.filterOptions.totalCases
                ? this.filterOptions.totalCases
                : this.filterOptions.pageSize * (this.filterOptions.pageNumber + 1);
    }

    changePageSize(event: any) {
        this.filterOptions.pageSize = event.value;
        this.filterOptions.pageNumber = 0;
        this.emitPaginationDetails();
    }

    getPageSizeOpts() {
        const rowTranslation = this.languageService.getTranslation('default-cm-rows') || 'Rows';
        this.pageSizeOptions = this.pageSizeOptions.map((opts) => ({
            ...opts,
            viewValue: `${opts.value} ${rowTranslation}`,
        }));
    }

    onClearAllFilters() {
        this.filterChipsConfig = [];
        this.filterData = [];
        this.filterObject = {};
        this.buttonOrDropdown = BUTTON;
        this.filterService.updateUrl(this.filterData, this.selectedTask, this.route);
        this.getUserTaskOnSelectedDropdown();
    }

    fetchAndUpdateCaseData() {
        const matchedColumn = this.currentTask?.columns.find((el: any) => el.name == 'reattemptStatus');
        if (!matchedColumn) {
            this.currentTask?.columns?.push({
                sort: false,
                name: 'reattemptStatus',
                label: 'Re-attempt status',
                labelType: null,
                mapping: 'recoveryStatus',
                mappingType: 'task_instance',
                type: '',
            });
            this.currentTask?.columns?.push({
                sort: false,
                name: 'processVariables',
                label: 'Process Variables',
                labelType: null,
                type: 'icon',
            });
            this.currentTask?.columns?.push({
                sort: false,
                name: 'taskVariables',
                label: 'Task Variables',
                labelType: null,
                type: 'icon',
            });
        }
    }

    ngOnDestroy() {}
}
